import type { PromoVideoProps } from "../../lib/types";
import {
  PROMO_TRILHAS,
  PROMO_PARTICIPATION_TYPES,
  getTrilhaTemplateDateLabel,
  type PromoTrilhaKey,
} from "../../lib/promo-form-config";

export type RoleKey = PromoVideoProps["role"];
export type PalestraType = NonNullable<PromoVideoProps["palestraType"]>;

export const ROLES = Object.fromEntries(
  PROMO_PARTICIPATION_TYPES.map(({ role, badgeLabel, color }) => [
    role,
    { label: badgeLabel, color },
  ]),
) as Record<RoleKey, { label: string; color: string }>;

const PROMO_PALESTRA_TYPES = Object.fromEntries(
  PROMO_TRILHAS.map(({ key, label, color }) => [key, { label: label.toUpperCase(), color }]),
) as Record<PromoTrilhaKey, { label: string; color: string }>;

const LEGACY_PALESTRA_TYPES = {
  palestra: { label: "PALESTRA", color: "#004c94" },
  mentoria: { label: "MENTORIA", color: "#c22821" },
  "community-lounge": { label: "COMMUNITY LOUNGE", color: "#e35100" },
  workshop: { label: "WORKSHOP", color: "#402b3c" },
  "forum-executivo": { label: "FÓRUM EXECUTIVO", color: "#1b5e20" },
  "forum-startup": { label: "FÓRUM STARTUP", color: "#6a1b9a" },
  "agentic-ai": { label: "AGENTIC AI", color: "#3e984c" },
  "javascript-e-node-js": { label: "JAVASCRIPT E NODE.JS", color: "#2e4837" },
  "quality-engineering-e-ai-testing": {
    label: "QUALITY ENGINEERING & AI TESTING",
    color: "#43273b",
  },
  "engenharia-de-ia-e-ml": { label: "ENGENHARIA DE IA E ML", color: "#3e984c" },
  "rh-agil": { label: "RH ÁGIL", color: "#c22821" },
  "gen-ai-e-llms": { label: "GEN AI & LLMS", color: "#3e984c" },
  "engenharia-de-dados-e-analytics": {
    label: "ENGENHARIA DE DADOS E ANALYTICS",
    color: "#3e984c",
  },
} as const;

/**
 * TDC activity formats and current-edition trilhas. Legacy format colors are
 * kept so older queued jobs still render; current form submissions use the
 * trilha keys below.
 */
export const PALESTRA_TYPES = {
  ...LEGACY_PALESTRA_TYPES,
  ...PROMO_PALESTRA_TYPES,
} satisfies Record<PalestraType, { label: string; color: string }>;

const BADGE_LABEL_MAX_FONT_SIZE = 46;
const BADGE_LABEL_MIN_FONT_SIZE = 30;
const BADGE_LABEL_MAX_TEXT_WIDTH = 820;
const BADGE_LABEL_CHAR_WIDTH_RATIO = 0.62;

export function getBadgeLabelTextStyle(label: string): {
  fontSize: number;
  whiteSpace: "nowrap";
} {
  const estimatedFontSize = Math.floor(
    BADGE_LABEL_MAX_TEXT_WIDTH / Math.max(1, label.length * BADGE_LABEL_CHAR_WIDTH_RATIO),
  );

  return {
    fontSize: Math.max(
      BADGE_LABEL_MIN_FONT_SIZE,
      Math.min(BADGE_LABEL_MAX_FONT_SIZE, estimatedFontSize),
    ),
    whiteSpace: "nowrap",
  };
}

/**
 * Resolve the active badge (label + color) for the given role + (optional)
 * palestra type.
 */
export function resolveBadge(
  role: RoleKey,
  palestraType: PalestraType | undefined,
): { label: string; color: string; dateLabel?: string; logoAsset?: string } {
  const trilha = PROMO_TRILHAS.find((option) => option.key === palestraType);
  if (trilha?.logoAsset) {
    return {
      label: "PATROCINADOR",
      color: trilha.color,
      logoAsset: trilha.logoAsset,
    };
  }
  if ((role === "palestrante" || role === "forum") && palestraType) {
    const palestraMeta = PALESTRA_TYPES[palestraType] ?? PALESTRA_TYPES.palestra;
    const dateLabel = getTrilhaTemplateDateLabel(role, palestraType);
    return dateLabel ? { ...palestraMeta, dateLabel } : palestraMeta;
  }
  const roleMeta = ROLES[role] ?? ROLES.palestrante;
  const dateLabel = getTrilhaTemplateDateLabel(role, palestraType);
  return dateLabel ? { ...roleMeta, dateLabel } : roleMeta;
}
